import * as Api from '@ViewModels';
import { AIConversationType } from '@ViewModels';
import { css, StyleDeclarationValue } from 'aphrodite';
import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { toast } from 'sonner';
import { IModalContext, ModalChildComponentContextKey } from '../../../models';
import { useErrorMessages } from '../../../models/hooks/appStateHooks';
import { useAccountProfileCreateMutation } from '../../../queries/AccountProfile/useAccountProfileCreateMutation';
import {
	invalidateAccountProfileQueries,
	useAccountProfileQuery,
} from '../../../queries/AccountProfile/useAccountProfileQuery';
import { useAccountProfileUpdateMutation } from '../../../queries/AccountProfile/useAccountProfileUpdateMutation';
import { invalidateAllAIConversationFilterQueries } from '../../../queries/AIConversation/useAIConversationFilterQuery';
import { useAdminAccountProfileRegenerateMutation } from '../../../queries/Admin/AccountProfile/useAdminAccountProfileRegenerateMutation';
import { hasContentPreferences, hasDiscreteContentPreferences } from '../../../utils/hasContentPreferences';
import { AIAssistantDialog } from '../../../web/components-v2/ai/AIAssistantDialog';
import { Button } from '../../../web/components-v2/Button';
import { Input } from '../../../web/components-v2/Input';
import { TextArea } from '../../../web/components-v2/TextArea';
import { LoadingSpinner } from '../../../web/components/LoadingSpinner';
import { asModalComponent } from '../../../web/components/Modal';
import { BusinessInfoDisplay } from '../../../web/components/BusinessInfoDisplay';
import { InfoIcon } from '../../../web/components/svgs/icons/InfoIcon';
import { brandPrimary } from '../../../web/styles/colors';
import { styles } from './styles';

interface IProps extends IModalContext<void> {
	account?: Api.IAccount;
	className?: string;
	styles?: StyleDeclarationValue[];
}

// Character limits for validation
const MAX_BUSINESS_DESCRIPTION_CHARS = 3000;
const MAX_CONTENT_GOALS_CHARS = 1500;
const MAX_KEYWORDS_CHARS = 9000;

const CustomContentPreferencesModalSFC: React.FC<IProps> = props => {
	const { account, className } = props;
	const id = React.useId();
	const [aiAssistantOpen, setAiAssistantOpen] = React.useState(false);
	const [isEditMode, setIsEditMode] = React.useState(false);
	const [descriptionValidationError, setDescriptionValidationError] = React.useState<string>('');
	const [contentGoalsValidationError, setContentGoalsValidationError] = React.useState<string>('');
	const [keywordsValidationError, setKeywordsValidationError] = React.useState<string>('');
	const [websiteUrl, setWebsiteUrl] = React.useState('');

	// Individual edit state for discrete fields
	const [editLocation, setEditLocation] = React.useState('');
	const [editAddress, setEditAddress] = React.useState('');
	const [editPhoneNumber, setEditPhoneNumber] = React.useState('');
	const [editPronouns, setEditPronouns] = React.useState('');
	const [editTonePreference, setEditTonePreference] = React.useState('');
	const [editContentGoals, setEditContentGoals] = React.useState('');
	const [editDescription, setEditDescription] = React.useState('');
	const [editKeywords, setEditKeywords] = React.useState('');
	const [isReconciling, setIsReconciling] = React.useState(false);

	// Pre-populate website URL from account if available
	React.useEffect(() => {
		if (account?.features?.websites?.websiteUrl && !websiteUrl) {
			setWebsiteUrl(account.features.websites.websiteUrl);
		}
	}, [account?.features?.websites?.websiteUrl, websiteUrl]);

	const impersonationContext = React.useMemo(() => ({ account }), [account]);
	const accountProfileQuery = useAccountProfileQuery({ impersonationContext });
	const errorMessages = useErrorMessages();

	const accountProfileCreateMutation = useAccountProfileCreateMutation({
		onError: error => {
			errorMessages.pushApiError(error);
		},
	});
	const accountProfileUpdateMutation = useAccountProfileUpdateMutation({
		onError: error => {
			errorMessages.pushApiError(error);
		},
	});
	const regenerateMutation = useAdminAccountProfileRegenerateMutation();

	const isSaving = accountProfileCreateMutation.isPending || accountProfileUpdateMutation.isPending;

	const accountName = account?.companyName || 'your account';
	const initialMessage = React.useMemo(() => {
		if (websiteUrl.trim()) {
			return `I'll help you configure Custom Content Preferences for "${accountName}." Analyzing the website now...`;
		}
		return `I'll help you configure Custom Content Preferences for "${accountName}." Checking their current settings...`;
	}, [accountName, websiteUrl]);

	const handleOpenChange = React.useCallback(
		(open: boolean) => {
			setAiAssistantOpen(open);
			if (!open) {
				// Invalidations are synchronous — refetches trigger on next query subscription
				invalidateAccountProfileQueries({ impersonationContext });
				// Clear website URL when dialog closes
				setWebsiteUrl('');
			} else {
				// When dialog opens, invalidate conversation filter queries to clear stale conversation cache
				invalidateAllAIConversationFilterQueries();
			}
		},
		[impersonationContext]
	);

	const customInitialAction = React.useMemo(() => {
		if (!websiteUrl.trim()) {
			return undefined;
		}
		return {
			_type: 'ContentSettingsIntakeAction',
			websiteUrl: websiteUrl.trim(),
		} as Api.ContentSettingsIntakeAction;
	}, [websiteUrl]);

	const profile = accountProfileQuery.data;
	const hasAnyData = hasContentPreferences(profile);

	// Contextual button text based on whether data exists
	const buttonText = hasAnyData ? 'Edit with AI Assistant' : 'Configure with AI Assistant';

	const profileHasDiscreteFields = hasDiscreteContentPreferences(profile);

	const populateEditState = (source: Api.AccountProfile | null | undefined) => {
		setEditLocation(source?.location || '');
		setEditAddress(source?.address || '');
		setEditPhoneNumber(source?.phoneNumber || '');
		setEditPronouns(source?.pronouns || '');
		setEditTonePreference(source?.tonePreference || '');
		setEditContentGoals(source?.contentGoals || '');
		setEditDescription(source?.businessDescription || '');
		setEditKeywords(source?.messagingPreferences?.keywords || '');
	};

	const handleEditDirectlyClick = async () => {
		setDescriptionValidationError('');
		setContentGoalsValidationError('');
		setKeywordsValidationError('');

		if (!profileHasDiscreteFields && profile?.businessDescription) {
			// Legacy data detected — auto-trigger reconcile
			setIsReconciling(true);
			setIsEditMode(true);
			try {
				const regenerated = await regenerateMutation.mutateAsync({ impersonationContext });
				if (!regenerated) {
					throw new Error('Empty response from regenerate endpoint');
				}
				populateEditState(regenerated);
				// Prefer existing keywords if regenerated response doesn't include them
				if (regenerated.messagingPreferences?.keywords === undefined && profile?.messagingPreferences?.keywords) {
					setEditKeywords(profile.messagingPreferences.keywords);
				}
			} catch (err) {
				// Fallback: open edit mode with what we have, show error feedback
				const detail = err instanceof Error ? err.message : '';
				toast.error(
					detail
						? `Failed to update content preferences: ${detail}. Please fill them in manually.`
						: 'Failed to update content preferences. Please fill them in manually.'
				);
				populateEditState(profile);
			} finally {
				setIsReconciling(false);
			}
		} else {
			// Discrete fields exist or no data at all — populate directly
			populateEditState(profile);
			setIsEditMode(true);
		}
	};

	const handleCancelEdit = () => {
		setIsEditMode(false);
		populateEditState(null);
		setDescriptionValidationError('');
		setContentGoalsValidationError('');
		setKeywordsValidationError('');
	};

	const handleDescriptionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
		const value = e.target.value;
		setEditDescription(value);
		if (value.length > MAX_BUSINESS_DESCRIPTION_CHARS) {
			setDescriptionValidationError(
				`Business description must be ${MAX_BUSINESS_DESCRIPTION_CHARS} characters or less`
			);
		} else {
			setDescriptionValidationError('');
		}
	};

	const handleContentGoalsChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
		const value = e.target.value;
		setEditContentGoals(value);
		if (value.length > MAX_CONTENT_GOALS_CHARS) {
			setContentGoalsValidationError(`Content goals must be ${MAX_CONTENT_GOALS_CHARS} characters or less`);
		} else {
			setContentGoalsValidationError('');
		}
	};

	const handleKeywordsChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
		const value = e.target.value;
		setEditKeywords(value);
		if (value.length > MAX_KEYWORDS_CHARS) {
			setKeywordsValidationError(`Keywords must be ${MAX_KEYWORDS_CHARS} characters or less`);
		} else {
			setKeywordsValidationError('');
		}
	};

	const hasValidationErrors =
		!!descriptionValidationError || !!contentGoalsValidationError || !!keywordsValidationError;

	const handleSave = async () => {
		const accountProfile: Api.AccountProfile = {
			id: accountProfileQuery.data?.id ?? null,
			businessDescription: editDescription,
			location: editLocation,
			address: editAddress,
			phoneNumber: editPhoneNumber,
			pronouns: editPronouns,
			tonePreference: editTonePreference,
			contentGoals: editContentGoals,
		};
		if (editKeywords) {
			accountProfile.messagingPreferences = { keywords: editKeywords };
		}

		try {
			await (accountProfile.id
				? accountProfileUpdateMutation.mutateAsync({ accountProfile, impersonationContext })
				: accountProfileCreateMutation.mutateAsync({ accountProfile, impersonationContext }));
			invalidateAccountProfileQueries({ impersonationContext });
			setIsEditMode(false);
			toast.success('Custom content preferences saved successfully');
		} catch {
			// Error already handled by mutation onError
		}
	};

	return (
		<div className={`${css(styles.container)} custom-content-preferences-modal ${className || ''}`}>
			<h1 className={css(styles.header)}>Custom Content Preferences</h1>
			<section className={css(styles.content)}>
				{accountProfileQuery.isPending ? (
					<div className={css(styles.loadingContainer)}>
						<LoadingSpinner />
					</div>
				) : accountProfileQuery.isError ? (
					<p className={css(styles.notConfiguredText)}>Error loading preferences. Please try again.</p>
				) : isEditMode ? (
					<>
						{isReconciling ? (
							<div className={css(styles.loadingContainer)} role='status' aria-live='polite'>
								<LoadingSpinner />
								<p className={css(styles.notConfiguredText)}>Converting your content preferences...</p>
							</div>
						) : (
							<>
								{/* Short fields in 2-column grid */}
								<div className={css(styles.editFormGrid)}>
									<div className={css(styles.fieldContainer)}>
										<label htmlFor={`${id}-location`} className={css(styles.infoFieldLabel)}>
											Location
										</label>
										<Input
											id={`${id}-location`}
											value={editLocation}
											onChange={e => setEditLocation(e.target.value)}
											placeholder='e.g., Seattle, WA'
										/>
									</div>
									<div className={css(styles.fieldContainer)}>
										<label htmlFor={`${id}-address`} className={css(styles.infoFieldLabel)}>
											Address
										</label>
										<Input
											id={`${id}-address`}
											value={editAddress}
											onChange={e => setEditAddress(e.target.value)}
											placeholder='e.g., 123 Main St'
										/>
									</div>
									<div className={css(styles.fieldContainer)}>
										<label htmlFor={`${id}-phone`} className={css(styles.infoFieldLabel)}>
											Phone Number
										</label>
										<Input
											id={`${id}-phone`}
											value={editPhoneNumber}
											onChange={e => setEditPhoneNumber(e.target.value)}
											placeholder='e.g., (555) 123-4567'
										/>
									</div>
									<div className={css(styles.fieldContainer)}>
										<label htmlFor={`${id}-pronouns`} className={css(styles.infoFieldLabel)}>
											Pronouns
										</label>
										<Input
											id={`${id}-pronouns`}
											value={editPronouns}
											onChange={e => setEditPronouns(e.target.value)}
											placeholder='e.g., We, I, They'
										/>
									</div>
									<div className={css(styles.fieldContainer)}>
										<label htmlFor={`${id}-tone`} className={css(styles.infoFieldLabel)}>
											Tone Preference
										</label>
										<Input
											id={`${id}-tone`}
											value={editTonePreference}
											onChange={e => setEditTonePreference(e.target.value)}
											placeholder='e.g., Professional, Friendly'
										/>
									</div>
								</div>

								{/* Full-width textareas */}
								<div className={css(styles.fieldContainer)}>
									<label htmlFor={`${id}-description`} className={css(styles.infoFieldLabel)}>
										Business Description
									</label>
									<TextArea
										id={`${id}-description`}
										value={editDescription}
										onChange={handleDescriptionChange}
										rows={6}
										maxLength={MAX_BUSINESS_DESCRIPTION_CHARS}
										placeholder='Enter business description'
									/>
									{descriptionValidationError && (
										<div className={css(styles.errorMessage)} role='alert' aria-live='polite'>
											{descriptionValidationError}
										</div>
									)}
									<div
										className={css(
											styles.characterCount,
											editDescription.length > MAX_BUSINESS_DESCRIPTION_CHARS && styles.characterCountError
										)}
									>
										{editDescription.length} / {MAX_BUSINESS_DESCRIPTION_CHARS} characters
									</div>
								</div>
								<div className={css(styles.fieldContainer)}>
									<label htmlFor={`${id}-goals`} className={css(styles.infoFieldLabel)}>
										Content Goals
									</label>
									<TextArea
										id={`${id}-goals`}
										value={editContentGoals}
										onChange={handleContentGoalsChange}
										rows={3}
										maxLength={MAX_CONTENT_GOALS_CHARS}
										placeholder='e.g., Increase brand awareness, Generate leads, Educate audience'
									/>
									{contentGoalsValidationError && (
										<div className={css(styles.errorMessage)} role='alert' aria-live='polite'>
											{contentGoalsValidationError}
										</div>
									)}
									<div
										className={css(
											styles.characterCount,
											editContentGoals.length > MAX_CONTENT_GOALS_CHARS && styles.characterCountError
										)}
									>
										{editContentGoals.length} / {MAX_CONTENT_GOALS_CHARS} characters
									</div>
								</div>
								<div className={css(styles.fieldContainer)}>
									<label htmlFor={`${id}-keywords`} className={css(styles.infoFieldLabel)}>
										Keywords
									</label>
									<TextArea
										id={`${id}-keywords`}
										value={editKeywords}
										onChange={handleKeywordsChange}
										rows={3}
										maxLength={MAX_KEYWORDS_CHARS}
										placeholder='Enter keywords separated by commas'
									/>
									{keywordsValidationError && (
										<div className={css(styles.errorMessage)} role='alert' aria-live='polite'>
											{keywordsValidationError}
										</div>
									)}
									<div
										className={css(
											styles.characterCount,
											editKeywords.length > MAX_KEYWORDS_CHARS && styles.characterCountError
										)}
									>
										{editKeywords.length} / {MAX_KEYWORDS_CHARS} characters
									</div>
								</div>
								<div className={css(styles.buttonContainer)}>
									<Button variant='primary' size='sm' onClick={handleSave} disabled={isSaving || hasValidationErrors}>
										Save
									</Button>
									<Button variant='secondary' size='sm' onClick={handleCancelEdit} disabled={isSaving}>
										Cancel
									</Button>
								</div>
							</>
						)}
					</>
				) : (
					<>
						{hasAnyData ? (
							profileHasDiscreteFields ? (
								<BusinessInfoDisplay profile={profile} />
							) : (
								<div className={css(styles.legacyNotice)}>
									<InfoIcon className={css(styles.legacyNoticeIcon)} fillColor={brandPrimary} />
									<span>
										This account has content preferences in an older format. We recommend updating them for the best
										experience.
									</span>
								</div>
							)
						) : (
							<>
								<p className={css(styles.notConfiguredText)}>Not configured</p>
								<div className={css(styles.customContentWebsiteUrl)}>
									<div className={css(styles.infoFieldLabel)}>
										Enter a website URL to help us generate your preferences based on your site
									</div>
									<Input
										type='url'
										value={websiteUrl}
										onChange={e => setWebsiteUrl(e.target.value)}
										placeholder='https://example.com'
									/>
								</div>
							</>
						)}
						<div className={css(styles.buttonContainer)}>
							<Button variant='secondary' size='sm' onClick={() => setAiAssistantOpen(true)}>
								{buttonText}
							</Button>
							<Button variant='secondary' size='sm' onClick={handleEditDirectlyClick}>
								{!profileHasDiscreteFields && profile?.businessDescription ? 'Update & Edit' : 'Edit Manually'}
							</Button>
						</div>
					</>
				)}
			</section>

			<AIAssistantDialog
				open={aiAssistantOpen}
				onOpenChange={handleOpenChange}
				assistantName='AccountOnboardAgent'
				conversationType={AIConversationType.AccountOnboarding}
				title='Custom Content Preferences'
				initialMessage={initialMessage}
				placeholder='Send a message...'
				impersonationContext={impersonationContext}
				customInitialAction={customInitialAction}
				dismissOnOutsideClick={false}
			/>
		</div>
	);
};

const CustomContentPreferencesModalAsObserver = observer(CustomContentPreferencesModalSFC);
const CustomContentPreferencesModalWithContext = inject(ModalChildComponentContextKey)(
	CustomContentPreferencesModalAsObserver
);
export const CustomContentPreferencesModal = asModalComponent(CustomContentPreferencesModalWithContext, {
	className: 'custom-content-preferences-modal',
	useDefaultHeader: true,
	shouldCloseOnOverlayClick: false,
});
